import React, { useEffect, useState } from 'react';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { useRouteMatch } from '../../../util/useRouteMatch';
import TreeNode, {
	TreeData,
	TreeItemData,
} from '../../layouts/SidePanel/TreeNode';
import QuotePolicyTree from './QuotePolicyTree';

export interface QuoteRoutes {
	ROOT: string;
	POLICY_DETAILS: string;
	EXPOSURES: string;
	PRICE_BREAKDOWN: string;
	FUTURE_INVOICES: string;
	INVOICES: string;
	AUX_DATA: string;
	DOCUMENTS: string;
	INVOICE: string;
	EXPOSURE: string;
	PERILS: string;
	PERIL: string;
	PAYMENTS: string;
	NEW_PAYMENT: string;
	PAYMENT: string;
	EDIT_PAYMENT: string;
}

export const QUOTE_ROUTES: QuoteRoutes = {
	ROOT: '/policy/:policyId/quotes/:quoteId',
	POLICY_DETAILS: '/policy/:policyId/quotes/:quoteId/details',
	EXPOSURES: '/policy/:policyId/quotes/:quoteId/exposures',
	PRICE_BREAKDOWN: '/policy/:policyId/quotes/:quoteId/pricing',
	FUTURE_INVOICES: '/policy/:policyId/quotes/:quoteId/future-invoices',
	INVOICES: '/policy/:policyId/quotes/:quoteId/invoices',
	AUX_DATA: '/policy/:policyId/quotes/:quoteId/auxData',
	DOCUMENTS: '/policy/:policyId/quotes/:quoteId/documents',
	INVOICE: '/policy/:policyId/quotes/:quoteId/invoices/:invoiceId',
	EXPOSURE: '/policy/:policyId/quotes/:quoteId/exposures/:exposureId',
	PERILS: '/policy/:policyId/quotes/:quoteId/exposures/:exposureId/perils',
	PERIL:
		'/policy/:policyId/quotes/:quoteId/exposures/:exposureId/perils/:perilId',
	PAYMENTS: '/policy/:policyId/quotes/:quoteId/payments',
	NEW_PAYMENT: '/policy/:policyId/quotes/:quoteId/payments/new',
	PAYMENT: '/policy/:policyId/quotes/:quoteId/payments/:paymentLocator',
	EDIT_PAYMENT:
		'/policy/:policyId/quotes/:quoteId/payments/:paymentLocator/edit',
};

interface Props {
	policyData: PolicyData;
	errorData: ErrorData;
	loading: boolean;
}

const QuoteSidebar = (props: Props) => {
	const { policyData, errorData } = props;
	const authData = useAuthContext();
	const { hasPermission } = authData;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const routeMatch = useRouteMatch(
		Object.keys(QUOTE_ROUTES).map(
			(key) => QUOTE_ROUTES[key as keyof QuoteRoutes],
		),
	);
	const params = routeMatch?.params || {};
	const routeName = routeMatch?.pattern?.path || '';
	const { selectedQuote } = policyData;

	const { currentErrorObject } = errorData;

	const getInitialExpandedItems = () => {
		const ids = ['Exposures'];
		// if looking at a certain exposure, expand that exposure and it's perils
		if (policyData.selectedExposure) {
			ids.push(policyData.selectedExposure.locator);
			ids.push(`${policyData.selectedExposure.locator}-Perils`);
		}

		return ids;
	};

	const [itemsExpanded, setItemsExpanded] = useState([]);

	useEffect(() => {
		const initialIds = getInitialExpandedItems();
		setItemsExpanded(initialIds);
	}, [policyData.selectedExposure, params.invoiceId, params.perilId]);

	const policyLink = `/policy/${selectedQuote.policyLocator}`;
	const quoteLink = `${policyLink}/quotes/${selectedQuote.locator}`;
	const priceBreakdownLink = `${quoteLink}/pricing`;
	const documentsLink = `${quoteLink}/documents`;
	const futureInvoicesLink = `${quoteLink}/future-invoices`;
	const invoicesLink = `${quoteLink}/invoices`;
	const paymentsLink = `${quoteLink}/payments`;

	const getRootItem = () => {
		const children = ['Policy Details', 'Exposures'];

		if (hasPermission('policy.quote')) {
			children.push('Price Breakdown');
		}

		if (
			selectedQuote.state === 'quoted' ||
			selectedQuote.state === 'accepted'
		) {
			children.push('Documents');
			if (hasPermission('invoice.fetch')) {
				children.push('Future Invoices');
				children.push('Invoices');
			}

			if (cashManagementEnabled && hasPermission('payment.fetch')) {
				children.push('Payments');
			}
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		};
	};

	const getItems = () => {
		const policyTree = QuotePolicyTree(
			policyData,
			itemsExpanded,
			currentErrorObject,
		);
		const items: TreeItemData = {
			root: getRootItem(),
			...policyTree,
			'Price Breakdown': {
				id: 'Price Breakdown',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Price Breakdown',
					link: priceBreakdownLink,
					selected: routeName === QUOTE_ROUTES.PRICE_BREAKDOWN,
				},
			},
			Documents: {
				id: 'Documents',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Documents',
					link: documentsLink,
					selected: routeName === QUOTE_ROUTES.DOCUMENTS,
				},
			},
			'Future Invoices': {
				id: 'Future Invoices',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Future Invoices',
					link: futureInvoicesLink,
					selected: routeName === QUOTE_ROUTES.FUTURE_INVOICES,
				},
			},
			Invoices: {
				id: 'Invoices',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Invoices',
					link: invoicesLink,
					selected:
						routeName === QUOTE_ROUTES.INVOICES ||
						routeName === QUOTE_ROUTES.INVOICE,
				},
			},
		};

		if (cashManagementEnabled) {
			items['Payments'] = {
				id: 'Payments',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Payments',
					link: paymentsLink,
					selected:
						routeName === QUOTE_ROUTES.PAYMENTS ||
						routeName === QUOTE_ROUTES.NEW_PAYMENT ||
						routeName === QUOTE_ROUTES.EDIT_PAYMENT ||
						routeName === QUOTE_ROUTES.PAYMENT,
				},
			};
		}

		return {
			...items,
		};
	};

	const initialItems = getItems();

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: initialItems,
		};

		return sidePanelData;
	};

	const tree = initializeTree();

	return <TreeNode treeData={tree} defaultExpanded={itemsExpanded} />;
};

export default withPolicyContext<Props>(QuoteSidebar);
